import { useQuery } from '@apollo/client';
import { useDialog } from 'components/hooks';
import useSiteMutations from 'components/Sites/hooks/useSiteMutations';
import { PLOTS_PER_PAGE } from 'constants/pagination';
import { GET_PLOTS_WITH_FARMS } from 'graphql/secure/queries/plot.queries';
import { GET_SITE } from 'graphql/secure/queries/site.queries';
import { IPlotsFilters } from 'graphql/secure/queries/types/plot-query.types';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { IPlot } from 'types/plot.types';
import { ESiteType, IFarmSite, ISite } from 'types/site.types';
import { GraphQlConnection } from 'types/types';
import { removeGraphConnections } from 'utils/graphql.utils';

interface IUseSiteProps {
  // Can be used to skip the useQueryParam search and use the internal search term instead
  skipPlotsQuery?: boolean;
}

const useSite = ({ skipPlotsQuery }: IUseSiteProps = {}) => {
  const { id } = useParams();
  const { openDialog } = useDialog();
  const { deleteSite } = useSiteMutations();

  // The cluster
  const { data, loading, error } = useQuery<{
    site: ISite;
  }>(GET_SITE, {
    variables: { id },
  });

  const {
    data: plotsData,
    loading: loadingPlots,
    error: plotError,
    fetchMore,
  } = useQuery<{ plots: GraphQlConnection<IPlot> }, { filters: IPlotsFilters; first: number }>(
    GET_PLOTS_WITH_FARMS,
    {
      variables: {
        filters:
          // it can never happen since the query gets skipped
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          data?.site?.siteType === ESiteType.CLUSTER ? { siteClusterId: id! } : { farmIds: [id!] },
        first: PLOTS_PER_PAGE,
      },
      skip:
        !id ||
        !data?.site ||
        (data?.site?.siteType !== ESiteType.CLUSTER && data?.site?.siteType !== ESiteType.FARM) ||
        skipPlotsQuery,
      notifyOnNetworkStatusChange: true,
    }
  );
  const hasClusterMorePlots: boolean = !!plotsData?.plots?.pageInfo?.hasNextPage;
  const totalPlotsCount: number = plotsData?.plots?.count || 0;

  const site = data?.site;

  const plots: IPlot[] = useMemo(
    () => (plotsData?.plots ? removeGraphConnections(plotsData.plots) : []),
    [plotsData]
  );

  const handleLoadMorePlots = () => {
    if (plotsData?.plots.pageInfo?.endCursor) {
      fetchMore({
        variables: {
          after: plotsData?.plots.pageInfo?.endCursor,
        },
      });
    }
  };

  const handleEditSite = (siteArg?: ISite) => {
    const siteItem = siteArg || site;

    if (siteItem?.siteType === ESiteType.CLUSTER) {
      return openDialog({
        type: 'ADD_EDIT_SITE_CLUSTER',
        props: {
          siteCluster: siteItem,
        },
      });
    }

    openDialog({
      type: 'ADD_EDIT_SITE',
      props: {
        site: siteItem,
      },
    });
  };

  const handleEditFarmPlots = (site: ISite | IFarmSite) => {
    if (site.siteType !== ESiteType.FARM) {
      throw new Error('This dialog can opened because the site is not a farm');
    }
    openDialog({
      type: 'ADD_EDIT_PLOTS',
      props: {
        farm: site,
      },
    });
  };

  const handleDeleteSite = (siteArg?: ISite) => {
    const siteItem = siteArg || site;
    if (!siteItem) return;

    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete site',
        text: 'Are you sure you want to delete this site? All data will be lost and you will not be able to recover this item.',
        submitText: 'Delete',
        itemTitle: siteItem.title,
        displayCloseButton: true,
        onSubmit: () => {
          deleteSite({ variables: { id: siteItem.id } });
        },
        onCancel: () => undefined,
      },
    });
  };

  return {
    site,
    loading,
    loadingPlots,
    error: error || plotError,
    hasClusterMorePlots,
    plots,
    totalPlotsCount,
    handleLoadMorePlots,
    handleEditFarmPlots,
    handleEditSite,
    handleDeleteSite,
  };
};

export default useSite;
